/**
 * RBAC Service
 * 角色权限管理 API 服务
 */
import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'

// ========== 类型定义 ==========

export interface Permission {
    id: number
    name: string
    description: string | null
    resource: string
    action: string
    is_active: boolean
    created_at: string
    updated_at: string
}

export interface PermissionGrouped {
    resource: string
    permissions: Permission[]
}

export interface Role {
    id: number
    name: string
    description: string | null
    priority: number
    scope_type: string
    scope_project_id?: number | null
    created_by?: number | null
    is_system: boolean
    is_active: boolean
    created_at: string
    updated_at: string
    permissions: Permission[]
}

export interface RoleWithUserCount extends Role {
    user_count: number
}

export interface PaginatedRoles {
    roles: RoleWithUserCount[]
    total: number
}

export interface GetRolesParams {
    skip?: number
    limit?: number
    name?: string
    roleNames?: string[]
}

export interface UserRole {
    user_id: number
    username: string
    email: string
    roles: Role[]
    effective_permissions: string[]
    can_review_history?: boolean
    can_export_history_dataset?: boolean
}

export interface CreatePermissionRequest {
    name: string
    resource: string
    action: string
    description?: string
}

export interface UpdatePermissionRequest {
    name?: string
    resource?: string
    action?: string
    description?: string
    is_active?: boolean
}

export interface CreateRoleRequest {
    name: string
    description?: string
    priority?: number
    scope_type?: string
    scope_project_id?: number | null
    permission_ids: number[]
}

export interface UpdateRoleRequest {
    name?: string
    description?: string
    priority?: number
    is_active?: boolean
    permission_ids?: number[]
}

export interface AssignRoleRequest {
    role_ids: number[]
}

// ========== 权限 API ==========

/**
 * 获取所有权限
 */
export const getPermissions = async (skip = 0, limit = 100): Promise<Permission[]> => {
    const response = await api.get<Permission[]>('/rbac/permissions', {
        params: { skip, limit }
    })
    return response.data
}

/**
 * 获取按资源分组的权限
 */
export const getPermissionsGrouped = async (): Promise<PermissionGrouped[]> => {
    const response = await api.get<PermissionGrouped[]>('/rbac/permissions/grouped')
    return response.data
}

/**
 * 获取权限详情
 */
export const getPermission = async (permissionId: number): Promise<Permission> => {
    const response = await api.get<Permission>(`/rbac/permissions/${permissionId}`)
    return response.data
}

/**
 * 创建权限
 */
export const createPermission = async (
    data: CreatePermissionRequest,
    options?: MutationRequestOptions
): Promise<Permission> => {
    const response = await api.post<Permission>(
        '/rbac/permissions',
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

/**
 * 更新权限
 */
export const updatePermission = async (
    permissionId: number,
    data: UpdatePermissionRequest,
    options?: MutationRequestOptions
): Promise<Permission> => {
    const response = await api.patch<Permission>(
        `/rbac/permissions/${permissionId}`,
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

/**
 * 删除权限
 */
export const deletePermission = async (
    permissionId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(
        `/rbac/permissions/${permissionId}`,
        getMutationRequestConfig(options)
    )
}

// ========== 角色 API ==========

/**
 * 获取所有角色（带用户数量）
 */
export const getRoles = async (params?: GetRolesParams): Promise<PaginatedRoles> => {
    const limit = params?.limit || 10
    const page = Math.floor((params?.skip || 0) / limit) + 1
    const requestParams = new URLSearchParams()
    requestParams.set('page', String(page))
    requestParams.set('page_size', String(limit))

    const normalizedName = params?.name?.trim()
    if (normalizedName) {
        requestParams.set('name', normalizedName)
    }

    params?.roleNames
        ?.map((roleName) => roleName.trim())
        .filter(Boolean)
        .forEach((roleName) => {
            requestParams.append('exact_names', roleName)
        })

    const response = await api.get<PaginatedRoles>('/rbac/roles', {
        params: requestParams
    })
    return response.data
}

/**
 * 获取活跃角色
 */
export interface GetActiveRolesParams {
    targetAccountType?: 'platform' | 'project'
}

export const getActiveRoles = async (
    params?: GetActiveRolesParams
): Promise<Role[]> => {
    const response = await api.get<Role[]>('/rbac/roles/active', {
        params: {
            target_account_type: params?.targetAccountType,
        },
    })
    return response.data
}

/**
 * 获取角色详情（含权限）
 */
export const getRole = async (roleId: number): Promise<Role> => {
    const response = await api.get<Role>(`/rbac/roles/${roleId}`)
    return response.data
}

/**
 * 创建角色
 */
export const createRole = async (
    data: CreateRoleRequest,
    options?: MutationRequestOptions
): Promise<Role> => {
    const response = await api.post<Role>(
        '/rbac/roles',
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

/**
 * 更新角色
 */
export const updateRole = async (
    roleId: number,
    data: UpdateRoleRequest,
    options?: MutationRequestOptions
): Promise<Role> => {
    const response = await api.patch<Role>(
        `/rbac/roles/${roleId}`,
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

/**
 * 删除角色
 */
export const deleteRole = async (
    roleId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(`/rbac/roles/${roleId}`, getMutationRequestConfig(options))
}

/**
 * 为角色分配权限
 */
export const assignPermissionsToRole = async (
    roleId: number,
    permissionIds: number[],
    options?: MutationRequestOptions
): Promise<Role> => {
    const response = await api.post<Role>(
        `/rbac/roles/${roleId}/permissions`,
        { permission_ids: permissionIds },
        getMutationRequestConfig(options)
    )
    return response.data
}

// ========== 用户角色 API ==========

/**
 * 获取用户角色和权限
 */
export const getUserRoles = async (userId: number): Promise<UserRole> => {
    const response = await api.get<UserRole>(`/rbac/users/${userId}/roles`)
    return response.data
}

export const getCurrentUserRoles = async (): Promise<UserRole> => {
    const response = await api.get<UserRole>('/rbac/users/me/roles')
    return response.data
}

/**
 * 为用户分配角色
 */
export const assignRolesToUser = async (
    userId: number,
    roleIds: number[],
    options?: MutationRequestOptions
): Promise<UserRole> => {
    const response = await api.post<UserRole>(
        `/rbac/users/${userId}/roles`,
        { role_ids: roleIds },
        getMutationRequestConfig(options)
    )
    return response.data
}

/**
 * 移除用户所有角色
 */
export const removeUserRoles = async (
    userId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(
        `/rbac/users/${userId}/roles`,
        getMutationRequestConfig(options)
    )
}

// ========== 健康检查 ==========

/**
 * RBAC 健康检查
 */
export const rbacHealthCheck = async (): Promise<{
    status: string
    total_permissions: number
    total_roles: number
    total_user_roles: number
}> => {
    const response = await api.get('/rbac/health')
    return response.data
}

/**
 * 初始化 RBAC 默认数据
 */
export const initializeRBAC = async (): Promise<{ message: string }> => {
    const response = await api.post('/rbac/initialize', undefined, getMutationRequestConfig())
    return response.data
}
